// ChartSection.tsx
import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import './ChartSection.scss'; // Asegúrate de tener un archivo CSS para estilos personalizados
import { Grid } from 'react-bootstrap-icons';

interface ChartSectionProps {
  showCombined: boolean;
  setShowCombined: (val: boolean) => void;
  startYear: number;
  setStartYear: (val: number) => void;
  endYear: number;
  setEndYear: (val: number) => void;
  years: number[];
  xAxisValues: string[];
  combinedSeries: any;
  series: any[];
  pieData: any[];
}

const ChartSection: React.FC<ChartSectionProps> = ({
  showCombined,
  setShowCombined,
  startYear,
  setStartYear,
  endYear,
  setEndYear,
  years,
  xAxisValues,
  combinedSeries,
  series,
  pieData,
}) => {
  // Color y modo reactivo
  const [colorMode, setColorMode] = useState(localStorage.getItem('colorMode') || 'dark');
  useEffect(() => {
    const interval = setInterval(() => {
      const mode = localStorage.getItem('colorMode') || 'dark';
      setColorMode((prev) => (prev !== mode ? mode : prev));
    }, 400);
    return () => clearInterval(interval);
  }, []);
  const textColor = colorMode === 'light' ? '#6b6b6b' : '#FFFFFF';
  const backgroundColor = 'transparent';
  const isMobile = window.innerWidth <= 900;

  // Estado para el rango de tiempo
  const [timeRange, setTimeRange] = useState< '1m' | '6m' | '1y'>('1m');
  const colorPalette = [
    '#FC7556', // Ventas Brutas (brand orange)
    '#E85B44', // Ventas Netas (darker orange)
    '#FFB388', // Propinas (peach)
    '#6b6b6b', // Pérdidas Netas (grey)
];

// antes de definir lineOption, ajusta así:
const gridMargin = isMobile
  ? { left: '24%', right: '10%', top: '15%', bottom: '25%' }
  : { left: '12%', right: '5%', top: '12%', bottom: '18%' };


  // === FILTRO DE RANGO DE TIEMPO PERSONALIZADO ===
  // Encuentra el mes actual en xAxisValues
  const today = new Date();
  const thisMonth = today.getMonth() + 1;
  const thisYear = today.getFullYear();
  // Helper para parsear 'MM/YY' a {month, year}
  const parseMY = (my: string) => {
    const [mm, yy] = my.split('/');
    return { month: parseInt(mm, 10), year: 2000 + parseInt(yy, 10) };
  };
  // Busca el índice del mes actual
  let currentIdx = xAxisValues.findIndex((my) => {
    const { month, year } = parseMY(my);
    return month === thisMonth && year === thisYear;
  });
  if (currentIdx === -1) {
    // Si no hay datos para el mes actual, toma el más cercano anterior
    const currentDateValue = new Date(thisYear, thisMonth - 1).getTime();
    currentIdx = xAxisValues.findIndex((my) => {
      const { month, year } = parseMY(my);
      return new Date(year, month - 1).getTime() > currentDateValue;
    });
    if (currentIdx === -1) currentIdx = xAxisValues.length - 1;
  }
  let customFilteredXAxis: string[] = [];
  if (timeRange === '1m') {
    customFilteredXAxis = currentIdx !== -1 ? [xAxisValues[currentIdx]] : [];
  } else if (timeRange === '6m') {
    // 4 pasados, actual, 1 futuro
    const start = Math.max(0, currentIdx - 4);
    const end = Math.min(xAxisValues.length, currentIdx + 2);
    customFilteredXAxis = xAxisValues.slice(start, end);
  } else if (timeRange === '1y') {
    // 10 pasados, actual, 1 futuro
    const start = Math.max(0, currentIdx - 10);
    const end = Math.min(xAxisValues.length, currentIdx + 2);
    customFilteredXAxis = xAxisValues.slice(start, end);
  }
  const customFilteredIndexes = customFilteredXAxis.map((my) => xAxisValues.indexOf(my));

  // Filtra series
  const filteredSeries = series.map((serie) => ({
    ...serie,
    data: customFilteredIndexes.map((i) => serie.data[i]),
  }));
  // Filtra combinedSeries
  const filteredCombinedSeries = {
    ...combinedSeries,
    data: customFilteredIndexes.map((i) => combinedSeries.data[i]),
  };
  // Pie chart: recalcula los valores solo para los meses visibles
  const filteredPieData = pieData.map((user, idx) => {
    // Suma solo los valores de los meses filtrados
    const serie = filteredSeries[idx];
    const value = serie ? serie.data.reduce((a: number, b: number) => a + (b ?? 0), 0) : 0;
    return {
      ...user,
      value,
    };
  });
  // Recalcula el total para porcentajes
  const totalPie = filteredPieData.reduce((a, b) => a + (b.value ?? 0), 0);
  const filteredPieDataWithPercent = filteredPieData.map((user) => ({
    ...user,
    label: user.label.replace(/\(.*\)/, `(${totalPie ? ((user.value / totalPie) * 100).toFixed(2) : '0'}%)`),
  }));

  // Opciones para el gráfico de líneas
  const lineOption = {
    color: colorPalette,
    tooltip: {
      trigger: 'item' as const, // Cambiado de 'axis' a 'item' para mostrar solo la serie sobre la que se posiciona
      formatter: (params: any) => {
        // params representa solo el punto de la serie sobre la que se posiciona
        return `${params.marker} ${params.seriesName}: ${params.data}`;
      },
    },
    legend: {
      show: !isMobile,      
      data: showCombined
        ? [combinedSeries.label]
        : series.map((s: any) => s.label),
      textStyle: { color: textColor, fontSize: isMobile ? 10 : 10 },
        itemWidth: isMobile ? 1 : 1,    // ancho del icono de cada ítem
  itemHeight: isMobile ? 6  : 2,
      bottom: isMobile ? 0 : 'auto',
      orient: 'horizontal' as const,
      itemGap: isMobile ? 8 : 4,
    },
grid: gridMargin,
    xAxis: {
      type: 'category' as const,
      name: 'Fecha',
      nameLocation: 'middle' as const,
      nameGap: 30,
      data: customFilteredXAxis,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: {
        color: textColor,
        rotate: isMobile ? 45 : 0,
        fontSize: isMobile ? 10 : 12,
      },
      nameTextStyle: { color: textColor },
      boundaryGap: false,
    },
    yAxis: {
      type: 'value' as const,
      name: 'Salario',
      nameLocation: 'middle' as const,
      nameGap: 60,
      axisLine: { lineStyle: { color: textColor } },
      axisLabel: { color: textColor },
      splitLine: {
        show: true,
        lineStyle: { color: colorMode === 'light' ? '#e0e0e0' : '#444444' },
      },
    },
    series: showCombined
      ? [
          {
            name: filteredCombinedSeries.label,
            type: 'line' as const,
            data: filteredCombinedSeries.data,
            smooth: true,
            connectNulls: true,
            lineStyle: { color: filteredCombinedSeries.color },
            itemStyle: { color: filteredCombinedSeries.color },
          },
        ]
      : filteredSeries.map((s: any) => ({
          name: s.label,
          type: 'line' as const,
          data: s.data,
          smooth: true,
          connectNulls: true,
          lineStyle: { color: s.color },
          itemStyle: { color: s.color },
        })),
    backgroundColor: backgroundColor,
  };

  // Opciones para el gráfico de pastel
  const pieOption = {
    color: colorPalette,
    tooltip: {
      trigger: 'item' as const,
      formatter: (params: any) => {
        return `${params.marker} ${params.name}: ${params.value}`;
      },
    },
    legend: {
      show:  false,
      orient: 'vertical' as const,
      left: 'right',
      textStyle: { color: textColor },
    },
    series: [
      {
        name: 'Proporción por usuario',
        type: 'pie' as const,
        radius: '60%',
        data: filteredPieDataWithPercent.map((d: any) => ({
          name: d.label,
          value: d.value,
          itemStyle: { color: d.color },
        })),
        label: {
          formatter: (params: any) => `${params.name}`,
          color: textColor,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      },
    ],
    backgroundColor: backgroundColor,
  };

  return (
    <div>
      {/* Sección Filtros + LineChart */}
      <div>
        <Row className="mb-3">
          <Col>
            <ButtonGroup>
              <Button
                variant="none"
                className={`time-range-button time-range-button-1m ${timeRange === '1m' ? 'active' : ''}`}
                onClick={() => setTimeRange('1m')}
              >
                1M
              </Button>
              <Button
                variant="none"
                className={`time-range-button time-range-button-6m ${timeRange === '6m' ? 'active' : ''}`}
                onClick={() => setTimeRange('6m')}
              >
                6M
              </Button>
              <Button
                variant="none"
                className={`time-range-button time-range-button-1y ${timeRange === '1y' ? 'active' : ''}`}
                onClick={() => setTimeRange('1y')}
              >
                1A
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
        
      </div>
      <Row className="mb-3">
        <Col xs={12} md={isMobile ? 12 : 6}>
          <div className="grafico-salarios-container">
            <ReactECharts option={lineOption} style={{ height: '300px', width: '100%' }} />
          </div>
        </Col>
        {/* Solo se renderiza el gráfico de pastel si no estamos en móvil */}
          <Col xs={12} md={6}>
        <div className="grafico-salarios-container">
          <ReactECharts option={pieOption} style={{ height: '430px', width: '100%' }} />
        </div>
          </Col>
      </Row>
    </div>
  );
};

export default ChartSection;
